// src/services/costs/contractGroups.js
// Vertragsgruppen einer BKP-Position (US-KOST-023, ADR 0064).
//
// Eine Position kann mehrere Vertraege tragen (z.B. zwei Vergaben mit
// unterschiedlichen Unternehmern und Summen). Ein Vertrag bildet mit seinen
// Nachtraegen eine GRUPPE; Zahlungen und Zahlungsanweisungen beziehen sich auf
// genau eine Gruppe (Vertragssumme, bisherige Zahlungen, Vertrag Nr.).
//
// Zuordnung:
//   - Nachtrag -> Vertrag  ueber `vertragId` (costEstimateContracts.VertragId)
//   - Zahlung  -> Vertrag  ueber `vertragId` (costEstimatePayments.VertragId)
// Altdaten ohne `vertragId` werden heuristisch zugeordnet: einzige Gruppe der
// Position, sonst eindeutiger Unternehmer, sonst eindeutige Vertragsnummer,
// sonst "nicht zugeordnet" (wird sichtbar gemacht statt still mitgezaehlt).
//
// Alle Funktionen sind rein (keine Persistenz) und werden von Detail-Panel und
// Zahlungsanweisungs-Modal gemeinsam genutzt.

import { formatSwissCurrency } from "./controlCalculations.js";

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const round2 = (v) => Math.round((num(v) + Number.EPSILON) * 100) / 100;
const sumBetrag = (list) => round2(list.reduce((s, x) => s + num(x.betrag), 0));

function resolveByHeuristics(item, groups) {
  if (groups.length === 0) return null;
  if (groups.length === 1) return groups[0];
  if (item.unternehmerId) {
    const m = groups.filter((g) => g.vertrag.unternehmerId === item.unternehmerId);
    if (m.length === 1) return m[0];
  }
  const nr = String(item.vertragNr || item.zaDaten?.vertragNr || "").trim();
  if (nr) {
    const m = groups.filter((g) => String(g.vertrag.vertragNr || "").trim() === nr);
    if (m.length === 1) return m[0];
  }
  return null;
}

/**
 * Gruppe eines Nachtrags oder einer Zahlung ermitteln.
 * @returns {object|null} Gruppe aus buildContractGroups() oder null
 */
export function resolveGroup(item, groups = []) {
  if (!item) return null;
  if (item.vertragId) {
    const g = groups.find((x) => x.id === item.vertragId);
    if (g) return g;
  }
  return resolveByHeuristics(item, groups);
}

/**
 * Vertraege einer Position zu Gruppen (Vertrag + Nachtraege) buendeln.
 * @returns {{ groups: Array<{id, vertrag, nachtraege, contracts, summe}>, unassigned: Array }}
 *   unassigned = Nachtraege, die keiner Gruppe zugeordnet werden konnten.
 */
export function buildContractGroups(contracts = []) {
  const groups = contracts
    .filter((c) => !c.istNachtrag)
    .map((v) => ({ id: v.id, vertrag: v, nachtraege: [], contracts: [v], summe: num(v.betrag) }));
  const unassigned = [];
  for (const n of contracts.filter((c) => c.istNachtrag)) {
    const g = resolveGroup(n, groups);
    if (g) {
      g.nachtraege.push(n);
      g.contracts.push(n);
    } else {
      unassigned.push(n);
    }
  }
  for (const g of groups) g.summe = sumBetrag(g.contracts);
  return { groups, unassigned };
}

/** Unternehmer einer Gruppe: der des Vertrags, sonst der erste eines Nachtrags. */
export function groupUnternehmer(g) {
  if (!g) return null;
  const src = [g.vertrag, ...g.nachtraege].find((c) => c.unternehmerId);
  return src ? { id: src.unternehmerId, name: src.unternehmerName || "" } : null;
}

/** Vertragsnummern einer Gruppe (Vertrag zuerst, distinct), als "4711, 4711-N1". */
export function groupVertragNr(g) {
  if (!g) return "";
  const nrs = g.contracts.map((c) => String(c.vertragNr || "").trim()).filter(Boolean);
  return Array.from(new Set(nrs)).join(", ");
}

/** Kurzbezeichnung fuer Tabellen: "Nr. 4711", sonst die Bezeichnung des Vertrags. */
export function groupShortLabel(g) {
  if (!g) return "";
  const v = g.vertrag;
  return v.vertragNr ? `Nr. ${v.vertragNr}` : v.bezeichnung || "Vertrag";
}

/** Ausfuehrliche Bezeichnung fuer Auswahllisten. */
export function groupLabel(g, { withSum = true } = {}) {
  if (!g) return "";
  const v = g.vertrag;
  const parts = [v.bezeichnung || "Vertrag"];
  if (v.vertragNr) parts.push(`Nr. ${v.vertragNr}`);
  if (v.unternehmerName) parts.push(v.unternehmerName);
  if (withSum) {
    const nachtraege = g.nachtraege.length ? ` (inkl. ${g.nachtraege.length} Nachtr.)` : "";
    parts.push(`CHF ${formatSwissCurrency(g.summe)}${nachtraege}`);
  }
  return parts.join(" · ");
}
